import { fromTheme, Icon, styled, Text, View } from 'lib_ui-primitives';
import { nativeOnlyProperties } from 'lib_ui-services';
import lodash from 'lodash';
import { createElement as rc } from 'react';
import buildServerAggregationFilters from '../../hooks/buildServerAggregationFilters';
import useDbView from '../../hooks/useDbView';
import useFilterMetadata from '../../hooks/useFilterMetadata';
import useServerAggregation from '../../hooks/useServerAggregation';
const { capitalize } = lodash;

const Label = styled(Text).attrs({ name: 'label' })`
    color: ${fromTheme('dataAggregation', 'fontColor')};
    font-size: ${({ centerLabelAbove, theme }) => (centerLabelAbove ? theme.fontSizeLabel : theme.fontSize)}px;
`;
Label.displayName = 'Label';

const AggregateText = styled(Text).attrs({ name: 'aggregate-text' })`
    color: ${fromTheme('dataAggregation', 'fontColor')};
    font-size: ${({ centerLabelAbove, theme }) => (centerLabelAbove ? theme.fontSizeLarge : theme.fontSize)}px;
`;
AggregateText.displayName = 'AggregateText';

const VerticalAggregateContainer = styled(View).attrs({ name: 'vertical-aggregate-container' })`
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-right: 6px;
    margin-left: 6px;
    margin-top: 6px;
    overflow: visible;
`;
VerticalAggregateContainer.displayName = 'VerticalAggregateContainer';

const HorizontalAggregateContainer = styled(View).attrs({ name: 'horizontal-aggregate-container' })`
    align-items: center;
    flex-direction: row;
    margin-right: 6px;
    margin-left: 6px;
`;
HorizontalAggregateContainer.displayName = 'HorizontalAggregateContainer';

const OfflineIcon = styled(Icon).attrs({ name: 'cloud-off', size: 'sm', color: 'currentColor' })`
    color: ${fromTheme('dataAggregation', 'fontColor')};
`;
OfflineIcon.displayName = 'OfflineIcon';

const _p = { useFilterMetadata, useServerAggregation, useDbView, buildServerAggregationFilters };
export const _private = _p;

export default function DataAggregation(props) {
    const {
        hNode,
        hNode: { namespace, relation, aggregateOperation, title, centerLabelAbove }
    } = props;
    if (aggregateOperation !== 'count') {
        throw new Error(
            `Aggregation operation of ${aggregateOperation} has not implemented yet. See dataView.DataAggregation.js`
        );
    }

    const metadataBasedFilters = _p.useFilterMetadata(hNode);
    const { viewCriteria } = _p.useDbView(namespace, relation, undefined, hNode);
    // Mirror the grid: forward the shared route view's scoping filters (inventory membership via
    // nestedListEntry, includedValues, excludeDeleted, searchCriteria, navigation-selection
    // criteria, …) to the server count so it is scoped the same way the grid is, instead of
    // counting every asset (#395 — Inventory > Count "Records" showed all assets by default).
    const sharedViewFilters = viewCriteria?.getFiltersFromView?.() ?? {};
    // requestFilters feeds only useServerAggregation, which serializes it internally to detect deep
    // changes — so a stable reference here buys nothing. Build it directly (no memo / JSON round-trip).
    const requestFilters = _p.buildServerAggregationFilters(metadataBasedFilters, sharedViewFilters);

    const { data, isOffline } = _p.useServerAggregation(namespace, relation, requestFilters);
    const recordCount = data?.[0]?.count ?? 0;

    const countDisplay = isOffline
        ? rc(OfflineIcon, null)
        : rc(AggregateText, null, recordCount.toString());

    // prettier-ignore
    return rc(centerLabelAbove ? VerticalAggregateContainer : HorizontalAggregateContainer, { id: props.hNode.id },
        rc(Label, {centerLabelAbove, ...nativeOnlyProperties({ellipsizeMode: 'clip', numberOfLines: 1})}, ((title + '') === '') ? `${capitalize(aggregateOperation)}:`: title),
        countDisplay
    );
}
